<template>
  <v-container>
    <v-dialog
      :value="value"
      @input="$emit('input', $event.target.value)"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card color="primary">
        <v-toolbar color="primary" flat>
          <v-toolbar-title>
            <h3>{{ isEdit ? "Editing Forklift Audit" : "New Forklift Audit" }}</h3>
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="
              $emit('input', false);
              reset();
            "
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-stepper v-model="currentStep" vertical class="elevation-0" v-if="value">
          <v-stepper-items>
            <v-stepper-step step="1" :complete="currentStep > 1" editable>Select a forklift</v-stepper-step>
            <v-stepper-content step="1">
              <v-form @submit.prevent="currentStep++">
                <v-card-text>
                  <v-select
                    @change="currentStep++"
                    class="mx-2"
                    box
                    label="Select a Forklift"
                    :items="forklifts"
                    v-model="body.lift"
                    color="secondary"
                  ></v-select>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat color="secondary lighten-2" type="submit">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>

            <v-stepper-step step="2" :complete="currentStep > 2" editable>Motor Off Checklist</v-stepper-step>
            <v-stepper-content step="2">
              <v-form @submit.prevent="confirmQuestions();">
                <v-card-text>
                  <span v-for="(question, index) in motorOffQuestions" :key="index">
                    <YesOrNo
                      :question="question"
                      :alternate="index % 2 ? false : true"
                      :index="index"
                      @data="handleOffQuestions"
                      :fillAnswer="isEdit ? body.motorOffQuestionnaire[index].responses : motorOffYes"
                    />
                  </span>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat color="success lighten-2" @click="setAllYes(true)">Yes To All</v-btn>
                  <v-btn flat color="secondary lighten-2" type="submit">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>

            <v-stepper-step
              step="3"
              :complete="currentStep > 3"
              editable
            >Maintenance Needed Category(s) (If Applicable)</v-stepper-step>
            <v-stepper-content step="3">
              <v-form @submit.prevent="currentStep++">
                <v-card-text>
                  <span v-for="(check, index) in motorOffChecks" :key="index">
                    <v-checkbox
                      v-model="body.motorOffMaintenanceLog[index]"
                      :label="check"
                      color="secondary lighten-2"
                      class="mt-0 pl-3"
                      hide-details
                      :value="check"
                    ></v-checkbox>
                  </span>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat color="secondary lighten-2" type="submit">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>

            <v-stepper-step
              step="4"
              :complete="currentStep > 4"
              editable
            >Motor On Checklist (Unusual Noises Must Be Investigated Immediately)</v-stepper-step>
            <v-stepper-content step="4">
              <v-form @submit.prevent="confirmQuestions();">
                <v-card-text>
                  <span v-for="(question, index) in motorOnQuestions" :key="index">
                    <YesOrNo
                      :question="question"
                      :alternate="index % 2 ? false : true"
                      :index="index"
                      @data="handleOnQuestions"
                      :fillAnswer="isEdit ? body.motorOnQuestionnaire[index].responses : motorOnYes"
                    />
                  </span>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat color="success lighten-2" @click="setAllYes(false)">Yes To All</v-btn>
                  <v-btn flat color="secondary lighten-2" type="submit">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>

            <v-stepper-step
              step="5"
              :complete="currentStep > 5"
              editable
            >Maintenance Needed Category(s) (If Applicable)</v-stepper-step>
            <v-stepper-content step="5">
              <v-form @submit.prevent="createOrEdit()">
                <v-card-text>
                  <span v-for="(check, index) in motorOnChecks" :key="index">
                    <v-checkbox
                      v-model="body.motorOnMaintenanceLog[index]"
                      :label="check"
                      color="secondary lighten-2"
                      class="mt-0 pl-3"
                      hide-details
                      :value="check"
                    ></v-checkbox>
                  </span>
                  <v-text-field
                    v-model="body.remarks"
                    label="Use this field to add any additional comments to this audit"
                    color="secondary"
                    box
                    class="mt-5"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat color="secondary lighten-2" type="submit">Create Audit</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import YesOrNo from "@/components/app/YesOrNo";
import { detailedDiff } from "deep-object-diff";

export default {
  name: "createForklift",

  props: ["value"],

  components: {
    YesOrNo
  },

  data() {
    return {
      isEdit: false,
      currentStep: 1,
      forklifts: ["Lift #1", "Lift #2", "Lift #3"],
      motorOffYes: null,
      motorOnYes: null,
      body: {
        lift: "",
        motorOffMaintenanceLog: [],
        motorOffQuestionnaire: [{ responses: [] }],
        motorOnQuestionnaire: [],
        motorOnMaintenanceLog: [],
        remarks: ""
      },
      motorOffChecklist: {
        questions: {
          motorOffQuestionnaire: "",
          motorOffChecks: ""
        }
      },
      motorOnChecklist: {
        questions: {
          motorOnQuestionnaire: "",
          motorOnChecks: ""
        }
      },
      editData: {}
    };
  },

  watch: {
    edit(val) {
      if (val._id) {
        this.editData = {
          auditor: val.auditor,
          lift: val.lift,
          motorOffMaintenanceLog: val.motorOffMaintenanceLog,
          motorOffQuestionnaire: val.motorOffQuestionnaire,
          motorOnQuestionnaire: val.motorOnQuestionnaire,
          motorOnMaintenanceLog: val.motorOnMaintenanceLog,
          remarks: val.remarks
        };

        this.body = {
          auditor: val.auditor,
          lift: val.lift,
          motorOffMaintenanceLog: val.motorOffMaintenanceLog,
          motorOffQuestionnaire: val.motorOffQuestionnaire,
          motorOnQuestionnaire: val.motorOnQuestionnaire,
          motorOnMaintenanceLog: val.motorOnMaintenanceLog,
          remarks: val.remarks
        };

        this.isEdit = true;
      }
    },
    async value(val) {
      if (val) {
        this.motorOffChecklist = await this.$store.dispatch(
          "audits/getQuestions",
          {
            module: "forklift",
            category: "motorOff"
          }
        );
        this.motorOnChecklist = await this.$store.dispatch(
          "audits/getQuestions",
          {
            module: "forklift",
            category: "motorOn"
          }
        );

        this.setQuestionsObject();
      }
    }
  },

  computed: {
    ...mapGetters({
      user: "app/getUser",
      edit: "audits/getEditAudit"
    }),

    motorOffQuestions() {
      return this.motorOffChecklist.questions.motorOffQuestionnaire || [];
    },
    motorOnQuestions() {
      return this.motorOnChecklist.questions.motorOnQuestionnaire || [];
    },
    motorOffChecks() {
      return this.motorOffChecklist.questions.motorOffChecks || [];
    },
    motorOnChecks() {
      return this.motorOnChecklist.questions.motorOnChecks || [];
    }
  },

  methods: {
    createOrEdit() {
      this.body.motorOffMaintenanceLog = this.body.motorOffMaintenanceLog.filter(
        item => {
          if (item) return item;
        }
      );
      this.body.motorOnMaintenanceLog = this.body.motorOnMaintenanceLog.filter(
        item => {
          if (item) return item;
        }
      );
      if (!this.confirmQuestions()) {
        if (!this.isEdit) {
          this.body.auditor = {
            name: this.user.displayName,
            badgeID: this.user.employeeID
          };
          this.$store.dispatch("audits/create", {
            module: "forklift",
            body: this.body
          });
        } else {
          this.body.editedBy = this.user.displayName;
          this.body.changed = this.getChanged(this.editData, this.body);
          this.$store.dispatch("audits/update", {
            module: "forklift",
            body: this.body,
            id: this.edit._id
          });
        }

        this.reset();
      } else if (this.body.lift === "") {
        this.$store.dispatch("app/setSnackbar", {
          text: "A forklift has not been selected.",
          color: "red"
        });
      } else {
        this.$store.dispatch("app/setSnackbar", {
          text: "Please confirm that all questions have been answered.",
          color: "red"
        });
      }
    },
    reset() {
      this.currentStep = 1;
      this.isEdit = false;
      this.$emit("input", false);
      this.$store.commit("audits/setEditAudit", []);
      this.$store.dispatch("audits/getAllAudits", {
        rowsPerPage: 10,
        sortBy: "dateCreated",
        descending: true,
        page: 1,
        from: null,
        to: null,
        search: null,
        module: "forklift"
      });
      this.body = {
        lift: "",
        motorOffMaintenanceLog: [],
        motorOffQuestionnaire: [{ responses: [] }],
        motorOnQuestionnaire: [],
        motorOnMaintenanceLog: [],
        remarks: ""
      };
    },
    setAllYes(motorOff) {
      if (motorOff) {

        this.motorOffYes = {
          yes: true,
          no: false,
          comments: ""
        }
        this.body.motorOffQuestionnaire.forEach(item => {
          item.responses = {
            yes: true,
            no: false,
            comments: ""
          }
        });
      } else {
        this.motorOnYes = {
          yes: true,
          no: false,
          comments: ""
        }
        this.body.motorOnQuestionnaire.forEach(item => {
          item.responses = {
            yes: true,
            no: false,
            comments: ""
          }
        });
      }
    },
    setQuestionsObject() {
      this.body.motorOffQuestionnaire = this.motorOffQuestions.map(question => {
        return {
          question: question,
          responses: {
            yes: false,
            no: false,
            comments: ""
          }
        };
      });

      this.body.motorOnQuestionnaire = this.motorOnQuestions.map(question => {
        return {
          question: question,
          responses: {
            yes: false,
            no: false,
            comments: ""
          }
        };
      });
    },
    handleOffQuestions(data) {
      this.body.motorOffQuestionnaire.forEach(item => {
        if (item.question === data.question) item.responses = data.val;
      });
    },
    handleOnQuestions(data) {
      this.body.motorOnQuestionnaire.forEach(item => {
        if (item.question === data.question) item.responses = data.val;
      });
    },
    confirmQuestions() {
      let disabledOff = this.body.motorOffQuestionnaire.map(question => {
        if (!question.responses) {
          return 1;
        } else if (question.responses.no && !question.responses.comments) {
          return 1;
        } else if (!question.responses.no && !question.responses.yes) {
          return 1;
        } else {
          return 0;
        }
      });
      let disabledOn = this.body.motorOnQuestionnaire.map(question => {
        if (!question.responses) {
          return 1;
        } else if (question.responses.no && !question.responses.comments) {
          return 1;
        } else if (!question.responses.no && !question.responses.yes) {
          return 1;
        } else {
          return 0;
        }
      });

      if (
        (disabledOff.includes(1) && parseInt(this.currentStep) === 2) ||
        (disabledOn.includes(1) && parseInt(this.currentStep) === 4) ||
        ((disabledOff.includes(1) || disabledOn.includes(1)) &&
          parseInt(this.currentStep) === 5)
      ) {
        this.$store.dispatch("app/setSnackbar", {
          text: "Please confirm that all questions have been answered.",
          color: "red"
        });
      } else {
        this.currentStep++;
      }

      if (disabledOn.includes(1) || disabledOff.includes(1)) {
        return true;
      } else {
        return false;
      }
    },

    getChanged(edit, body) {
      let oldObj = {
        lift: edit.lift,
        auditor: edit.auditor,
        motorOnMaintenanceLog: edit.motorOnMaintenanceLog,
        motorOnQuestionnaire: edit.motorOnQuestionnaire,
        motorOffQuestionnaire: edit.motorOffQuestionnaire,
        motorOffMaintenanceLog: edit.motorOffMaintenanceLog,
        remarks: edit.remarks
      };

      let newObj = {
        lift: body.lift,
        auditor: body.auditor,
        motorOnMaintenanceLog: body.motorOnMaintenanceLog,
        motorOnQuestionnaire: body.motorOnQuestionnaire,
        motorOffQuestionnaire: body.motorOffQuestionnaire,
        motorOffMaintenanceLog: body.motorOffMaintenanceLog,
        remarks: body.remarks
      };

      return detailedDiff(oldObj, newObj);
    }
  }
};
</script>

<style>
</style>
